import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Send, Loader2, Sparkles, User, Bot } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSendChat, type ChatMessage } from "@/api/queries";
import { cn } from "@/lib/utils";

const WELCOME: ChatMessage = {
  id: "welcome",
  role: "assistant",
  content:
    "Привет. Я aiZam agent. Помогу с аналитическими сборами данных: контрагенты, домены, телефоны, утечки, OSINT-разведка. Задай вопрос или укажи цель сбора.",
  timestamp: new Date().toISOString(),
  status: "ok",
};

export function Chat() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const send = useSendChat();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const onSend = async () => {
    const text = input.trim();
    if (!text || isThinking) return;
    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setIsThinking(true);

    try {
      const history = messages
        .filter((m) => m.id !== "welcome")
        .slice(-20)
        .map((m) => ({ role: m.role, content: m.content }));
      const r = await send.mutateAsync({ message: text, history });
      const assistantMsg: ChatMessage = {
        id: `a-${Date.now()}`,
        role: "assistant",
        content: r.message?.content || r.content || "—",
        timestamp: new Date().toISOString(),
        status: r.error ? "error" : "ok",
        model: r.model,
        tokens: r.usage?.total_tokens || r.usage?.completion_tokens,
      };
      setMessages((m) => [...m, assistantMsg]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          id: `e-${Date.now()}`,
          role: "assistant",
          content: `Ошибка: ${(e as any)?.response?.data?.error?.message || (e as Error).message || "unknown"}`,
          timestamp: new Date().toISOString(),
          status: "error",
        },
      ]);
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          aiZam Chat
        </h1>
      </div>

      <Card className="h-[calc(100vh-280px)] flex flex-col">
        <CardHeader className="pb-3 shrink-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" /> aiZam agent
          </CardTitle>
        </CardHeader>

        <CardContent ref={scrollRef} className="flex-1 overflow-y-auto space-y-4">
          {messages.map((m) => (
            <div
              key={m.id}
              className={cn("flex gap-3", m.role === "user" ? "justify-end" : "justify-start")}
            >
              {m.role === "assistant" && (
                <div className="rounded-full bg-primary/10 p-2 h-8 w-8 shrink-0 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
              )}
              <div
                className={cn(
                  "rounded-lg px-4 py-2 max-w-[75%]",
                  m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted",
                  m.status === "error" && "border border-red-500",
                )}
              >
                <div className="whitespace-pre-wrap text-sm">{m.content}</div>
                <div className="flex items-center gap-2 mt-1 text-xs opacity-70">
                  <span>{new Date(m.timestamp).toLocaleTimeString("ru-RU")}</span>
                </div>
              </div>
              {m.role === "user" && (
                <div className="rounded-full bg-muted p-2 h-8 w-8 shrink-0 flex items-center justify-center">
                  <User className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
          {isThinking && (
            <div className="flex gap-3">
              <div className="rounded-full bg-primary/10 p-2 h-8 w-8 shrink-0 flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="rounded-lg px-4 py-2 bg-muted flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> думаю…
              </div>
            </div>
          )}
        </CardContent>

        <div className="p-3 border-t shrink-0">
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              onSend();
            }}
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Спросить aiZam agent"
              disabled={isThinking}
            />
            <Button type="submit" disabled={isThinking || !input.trim()}>
              {isThinking ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Отправить
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
